// src/ai TImerOnHeader/DaySessionContext.js (Full Complete File)

import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  useToast, 
  Button, 
  Box, 
  HStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure
} from '@chakra-ui/react';
import { TimeIcon } from '@chakra-ui/icons';
import { getDaySessionStatus, startDaySession, stopDaySession } from '../TimerOnHeader/TimerOnHeaderServices/TimerApi';

const DaySessionContext = createContext();

export const useDaySession = () => {
  const context = useContext(DaySessionContext);
  if (!context) {
    throw new Error('useDaySession must be used within a DaySessionProvider');
  }
  return context;
};

export const DaySessionProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const fetchCurrentStatus = async () => {
    try {
      const res = await getDaySessionStatus();
      if (res.data?.status === true && res.data?.data?.today) {
        const dayStatus = res.data.data.today.status;
        const isStarted = (dayStatus?.trim().toLowerCase() === 'started');
        setIsActive(isStarted);
        return isStarted;
      } else {
        setIsActive(false);
        return false;
      }
    } catch (err) {
      console.error('❌ Error fetching status:', err);
      setIsActive(false);
      toast({
        title: 'Error',
        description: 'Failed to fetch status',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  };

  useEffect(() => {
    fetchCurrentStatus().finally(() => {
      setLoading(false);
    });
  }, []);

  const handleToggle = async () => {
    setLoading(true);
    try {
      let success = false;

      if (isActive) {
        console.log('🛑 Stopping day...');
        const response = await stopDaySession();
        console.log('Stop Response:', response.data);

        if (response.data?.status === true) {
          toast({ 
            title: 'Day Stopped', 
            description: response.data?.message || 'Work session ended', 
            status: 'info', 
            duration: 2000, 
            isClosable: true 
          });
          success = true;

          const currentPath = window.location.pathname;
          const isOnDetailPage = currentPath.startsWith('/home/ap/board-details/') 
                                 && currentPath !== '/home/ap/board-details';

          if (isOnDetailPage) {
            console.log('🔄 User on board detail page → redirecting to list');
            window.location.href = '/home/ap/project-board';
            return;
          }
        } else {
          toast({ 
            title: 'Error', 
            description: response.data?.message || 'Failed to stop', 
            status: 'error', 
            duration: 3000, 
            isClosable: true 
          });
        }
      } else {
        console.log('▶️ Starting day...');
        const response = await startDaySession();
        console.log('Start Response:', response.data);

        if (response.data?.status === true) {
          toast({ 
            title: 'Day Started', 
            description: response.data?.message || 'Work session started', 
            status: 'success', 
            duration: 2000, 
            isClosable: true 
          });
          success = true;
        } else {
          toast({ 
            title: 'Notice', 
            description: response.data?.message || 'Day already running', 
            status: 'warning', 
            duration: 3000, 
            isClosable: true 
          });
        }
      }

      if (success) {
        console.log('🔄 Fetching updated status...');
        await new Promise(resolve => setTimeout(resolve, 1500));

        let updated = await fetchCurrentStatus();
        if (!updated && !isActive) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          await fetchCurrentStatus();
        }
      }

    } catch (error) {
      console.error('❌ Toggle Error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Request failed',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      await new Promise(resolve => setTimeout(resolve, 1000));
      await fetchCurrentStatus();
    } finally {
      setLoading(false);
    }
  };

  const value = {
    isActive,
    loading,
    handleToggle,
    fetchCurrentStatus,
  };

  return (
    <DaySessionContext.Provider value={value}>
      {children}
    </DaySessionContext.Provider>
  );
};

// 🎨 Icon Dot Indicator Button with Confirmation Dialog
export const DaySessionButton = () => {
  const { isActive, loading, handleToggle } = useDaySession();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();

  const onConfirm = () => {
    onClose();
    handleToggle();
  };

  return (
    <>
      <Button
        onClick={onOpen}
        isLoading={loading}
        size="md"
        height="42px"
        px={5}
        fontSize="sm"
        fontWeight="500"
        borderRadius="md"
        bg={isActive ? 'red.500' : 'green.500'}
        color="white"
        _hover={{
          bg: isActive ? 'red.600' : 'green.600',
        }}
        transition="all 0.2s"
      >
        <HStack spacing={2}>
          <TimeIcon boxSize={4} />
          <span>{isActive ? 'Stop Day' : 'Start Day'}</span>
          <Box
            width="6px"
            height="6px"
            borderRadius="full"
            bg="white"
            opacity={isActive ? 1 : 0}
            sx={{
              animation: isActive ? 'blink 1.5s ease-in-out infinite' : 'none',
              '@keyframes blink': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.3 },
              },
            }}
          />
        </HStack>
      </Button>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {isActive ? 'Stop Day Session' : 'Start Day Session'}
            </AlertDialogHeader>

            <AlertDialogBody>
              {isActive 
                ? 'Are you sure you want to stop your work session? Your progress will be saved.' 
                : 'Are you sure you want to start your work session?'}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                No
              </Button>
              <Button 
                colorScheme={isActive ? 'red' : 'green'} 
                onClick={onConfirm} 
                ml={3}
              >
                Yes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};